import { useId, type ReactElement, type ReactNode } from 'react'
import { Toggle as BaseToggle } from '@base-ui/react/toggle'
import { ToggleGroup as BaseToggleGroup } from '@base-ui/react/toggle-group'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip } from './Tooltip'
import './toggle-group.css'

export type ToggleGroupItem<Value extends string> = {
  value: Value
  /** Visible text, or the accessible name and tooltip when the group is icon-only, or the name of an option drawn from `content`. */
  label: string
  /** A fuller name when the visible label is terse, for example “Automatic columns” for “Auto”. */
  accessibleLabel?: string
  /** A Lucide icon. Required when the group is icon-only. */
  icon?: ReactElement
  /**
   * Drawn in place of the icon and label, such as a host’s avatar, name and how many are on their stage. The option is
   * named by `accessibleLabel`, else `label`, which should say everything the content shows (a button’s content isn’t
   * read). Lead with an Avatar in a `pill` group and it sits a step from the edge.
   */
  content?: ReactNode
  /** A tooltip naming the option, for content that shows less than its name. Icon-only options show their label. */
  tooltip?: string
  disabled?: boolean
}

/**
 * How the options sit. `segmented`: on a neutral track, as Multiview’s columns. `pill`: a pill-shaped track and
 * options in one row that never wraps, for people or places, such as the watch screen’s stages. `list`: a row each
 * down a sheet or panel, with no track; Up and Down move between them, and the chosen row takes the pink selection
 * surface and a check.
 */
export type ToggleGroupVariant = 'segmented' | 'pill' | 'list'

/** One option is always chosen; pressing the chosen option keeps it. */
export type ToggleGroupRequiredValue<Value extends string> = {
  allowNone?: false
  value: Value
  onValueChange: (value: Value) => void
}

/** Pressing the chosen option clears the choice, for filters where “no filter” is valid. */
export type ToggleGroupOptionalValue<Value extends string> = {
  allowNone: true
  value: Value | null
  onValueChange: (value: Value | null) => void
}

type ToggleGroupBaseProps<Value extends string> = {
  /** Names the group. Shown at the start of the control unless hidden. */
  label: string
  hideLabel?: boolean
  /** A fuller name for assistive technology when the visible label is terse, for example “Multiview columns” for “Columns”. */
  accessibleLabel?: string
  items: ToggleGroupItem<Value>[]
  /** Show only icons, with each label as the accessible name and tooltip. */
  iconOnly?: boolean
  /** Span the container and share its width equally between the options, for example a Grid / Map switch on a phone. */
  fill?: boolean
  /** `segmented` (default), `pill` or `list`: see ToggleGroupVariant. */
  variant?: ToggleGroupVariant
  className?: string
}

export type ToggleGroupProps<Value extends string> = ToggleGroupBaseProps<Value> & (ToggleGroupRequiredValue<Value> | ToggleGroupOptionalValue<Value>)

// Overloads rather than one union parameter, so `Value` is inferred from the branch in use (a nullable state with
// `allowNone`, a plain one without) exactly as before `allowNone` existed.
/** A segmented control: one option from a few, such as the Multiview column count, a row of stages (`pill`) or a list
 *  of them (`list`). */
export function ToggleGroup<Value extends string>(props: ToggleGroupBaseProps<Value> & ToggleGroupRequiredValue<Value>): ReactElement
export function ToggleGroup<Value extends string>(props: ToggleGroupBaseProps<Value> & ToggleGroupOptionalValue<Value>): ReactElement
export function ToggleGroup<Value extends string>(props: ToggleGroupProps<Value>) {
  const { label, hideLabel = false, accessibleLabel, items, iconOnly = false, fill = false, variant = 'segmented', className } = props
  const list = variant === 'list'
  const labelId = useId()
  const showLabel = !hideLabel
  // A fuller accessible name replaces the visible label as the group's name, so the label isn't read twice.
  const renderLabel = showLabel || !accessibleLabel
  return (
    <BaseToggleGroup
      value={props.value === null ? [] : [props.value]}
      onValueChange={(next) => {
        const [chosen] = next
        if (chosen !== undefined) props.onValueChange(chosen as Value)
        else if (props.allowNone) props.onValueChange(null)
      }}
      orientation={list ? 'vertical' : 'horizontal'}
      aria-label={accessibleLabel}
      aria-labelledby={accessibleLabel ? undefined : labelId}
      className={cn(
        'play-toggle-group',
        variant !== 'segmented' && `play-toggle-group--${variant}`,
        fill && 'play-toggle-group--fill',
        fill && showLabel && 'play-toggle-group--labelled',
        className,
      )}
    >
      {renderLabel && (
        <span
          id={accessibleLabel ? undefined : labelId}
          aria-hidden={accessibleLabel ? true : undefined}
          className={showLabel ? 'play-toggle-group__label' : 'sr-only'}
        >
          {label}
        </span>
      )}
      {items.map((item) => {
        const drawn = item.content !== undefined
        const toggle = (
          <BaseToggle
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            aria-label={iconOnly ? item.label : drawn ? (item.accessibleLabel ?? item.label) : item.accessibleLabel}
            className={cn('play-toggle', iconOnly && 'play-toggle--icon')}
          >
            {drawn ? (
              item.content
            ) : (
              <>
                {item.icon}
                {!iconOnly && <span>{item.label}</span>}
              </>
            )}
            {list && <Check className="play-toggle__check" aria-hidden="true" />}
          </BaseToggle>
        )
        const tooltip = iconOnly ? item.label : item.tooltip
        return tooltip ? <Tooltip key={item.value} content={tooltip}>{toggle}</Tooltip> : toggle
      })}
    </BaseToggleGroup>
  )
}
